import { useState } from 'react'
import { bandFromScore, formatDate } from '../../lib/format'
import type { Brief, CaseStudy } from '../../types'
import { ContactCard, CopyButton, Gauge, MeetingPrepPanel } from './parts'

/** The five persuasion-engine dimensions, in the order the prototype displayed them. */
const SCORE_LABELS = [
  ['differentiation', 'Differentiation'],
  ['curiosity', 'Curiosity'],
  ['commercial', 'Commercial'],
  ['executive', 'Executive appeal'],
  ['response', 'Response'],
] as const

/** The full 11-section account brief. Section order and numbering match the prototype exactly. */
export function BriefBody({ brief, cases }: { brief: Brief; cases: CaseStudy[] }) {
  const j = brief.full
  const score = brief.score ?? 0
  const band = j.band ?? bandFromScore(score)

  const variants = j.outreach?.variants ?? []
  const winner =
    Number.isInteger(j.outreach?.winner) && j.outreach!.winner! >= 0 && j.outreach!.winner! < variants.length
      ? j.outreach!.winner!
      : 0
  const [active, setActive] = useState(winner)

  const prep = j.meetingPrep
  const orgmap = j.orgmap ?? {}

  const total = (i: number) => {
    const v = variants[i]
    if (v?.total != null) return v.total
    const s = v?.scores ?? {}
    return (s.differentiation ?? 0) + (s.curiosity ?? 0) + (s.commercial ?? 0) + (s.executive ?? 0) + (s.response ?? 0)
  }

  const shown = variants[active]

  return (
    <div id="brief" className="on">
      <div className="briefhead">
        <div className="bh-row">
          <div className="bh-meta">
            <div className="bh-sector">{brief.sector}</div>
            <div className="bh-name">{brief.company}</div>
            <div className="bh-web">
              {brief.website && (
                <>
                  <a
                    href={`https://${brief.website.replace(/^https?:\/\//, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {brief.website}
                  </a>{' '}
                  ·{' '}
                </>
              )}
              Account brief · generated {formatDate(brief.updatedAt)}
            </div>
          </div>
          <Gauge score={score} band={band} size={118} />
        </div>
      </div>

      <div className="briefbody">
        {prep && <MeetingPrepPanel prep={prep} />}

        <Section n="01" title="Company Summary">
          <p className="body">{j.summary}</p>
        </Section>

        <Section n="02" title="Current Strategic Priorities">
          <ul className="clean">
            {(j.priorities ?? []).map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Section>

        <Section n="03" title="Transformation Signals">
          <ul className="clean siglist">
            {(j.signals ?? []).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </Section>

        <Section n="04" title="Opportunity Score">
          <div className="sbk">
            {(j.scoreBreakdown ?? []).map((r, i) => (
              <div className="sbk-row" key={i}>
                <span className="sbk-sig">{r.sig}</span>
                <span className="sbk-bar">
                  <i style={{ width: `${Math.min(100, (r.pts / 25) * 100)}%` }} />
                </span>
                <span className="sbk-pts">+{r.pts}</span>
              </div>
            ))}
          </div>
          <div className="reason">{j.scoreReason}</div>
        </Section>

        <Section n="05" title="Relevant Burendo Experience">
          <p className="body">{j.experience}</p>
        </Section>

        <Section n="06" title="Top 5 Matching Case Studies">
          {(j.matches ?? []).map((m, i) => {
            // Tags and the "restricted" flag come from the local library, not the model.
            const cs = cases.find((c) => c.id === m.id)
            const tags = (cs?.tags ?? '').split(',').slice(0, 5).filter(Boolean)
            return (
              <div className="cs" key={i}>
                <div className="cs-top">
                  <div>
                    <span className="cs-client">{m.client || cs?.client}</span>
                    {cs?.restricted && <span className="badge-r">Restricted</span>}
                    <div className="cs-title">{m.title || cs?.title}</div>
                  </div>
                  <div className="cs-sim">
                    <div className="pct">{m.similarity}%</div>
                    <div className="l">Match</div>
                  </div>
                </div>
                <div className="cs-why">{m.why}</div>
                <div className="cs-tags">
                  {tags.map((t, k) => (
                    <span className="tag" key={k}>
                      {t.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )
          })}
        </Section>

        <Section n="07" title="Technology & Product Org Map">
          <div className="orgleaders">
            {(orgmap.leaders ?? []).length ? (
              orgmap.leaders!.map((c, i) => <ContactCard contact={c} key={i} />)
            ) : (
              <div className="cc">
                <div className="cc-name">No senior technology / product leader identified publicly</div>
              </div>
            )}
          </div>
          <div className="orgcols">
            <div className="orgcol">
              <div className="orgcol-h">Technology</div>
              {(orgmap.technology ?? []).length ? (
                orgmap.technology!.map((c, i) => <ContactCard contact={c} key={i} />)
              ) : (
                <div className="empty" style={{ padding: '14px 0' }}>
                  No roles identified
                </div>
              )}
            </div>
            <div className="orgcol">
              <div className="orgcol-h">Product &amp; Delivery</div>
              {(orgmap.product ?? []).length ? (
                orgmap.product!.map((c, i) => <ContactCard contact={c} key={i} />)
              ) : (
                <div className="empty" style={{ padding: '14px 0' }}>
                  No roles identified
                </div>
              )}
            </div>
          </div>
          <div className="orgmeta">
            {orgmap.emailFormat && (
              <>
                Email format: <code>{orgmap.emailFormat}</code>
              </>
            )}
            {orgmap.domain && (
              <>
                {' '}
                &nbsp;·&nbsp; domain <code>{orgmap.domain}</code>
              </>
            )}
            {orgmap.generalContact && (
              <>
                {' '}
                &nbsp;·&nbsp; general enquiries <code>{orgmap.generalContact}</code>
              </>
            )}
          </div>
          <div className="bnote">
            Names and emails are <b>inferred</b> from public sources and the company's email pattern —
            unverified starting points, not confirmed contacts. Verify each address and ensure a lawful basis
            before outreach. Roles shown without a name are best resolved via LinkedIn Sales Navigator.
          </div>
        </Section>

        <Section n="08" title="Suppliers & Procurement Frameworks">
          <div className="orgcols">
            <div className="orgcol">
              <div className="orgcol-h">Incumbent suppliers / partners</div>
              {(j.suppliers ?? []).length ? (
                j.suppliers!.map((s, i) => (
                  <div className="cc" key={i}>
                    <div className="cc-name">{s.name || s.scope || '—'}</div>
                    {s.name && s.scope && <div className="cc-role">{s.scope}</div>}
                    {s.note && <div className="cc-why">{s.note}</div>}
                  </div>
                ))
              ) : (
                <div className="empty" style={{ padding: '10px 0' }}>
                  No incumbent suppliers identified in public sources — worth confirming.
                </div>
              )}
            </div>
            <div className="orgcol">
              <div className="orgcol-h">Procurement frameworks</div>
              {(j.frameworks ?? []).length ? (
                j.frameworks!.map((f, i) => (
                  <div className="cc" key={i}>
                    <div className="cc-name">
                      {f.name}
                      {f.type && <span className="captag"> {f.type}</span>}
                    </div>
                    {f.note && <div className="cc-why">{f.note}</div>}
                  </div>
                ))
              ) : (
                <div className="empty" style={{ padding: '10px 0' }}>
                  No procurement frameworks identified — confirm the route to market.
                </div>
              )}
            </div>
          </div>
          <div className="bnote">
            Incumbents and frameworks are starting points from public sources — confirm against contract
            registers (Contracts Finder, CCS, Companies House) before relying on them.
          </div>
        </Section>

        <Section n="09" title="Suggested Burendo Services">
          <div className="two">
            {(j.services ?? []).map((s, i) => (
              <div className={`svc c${i % 4}`} key={i}>
                <div className="nm">{s.service}</div>
                <div className="rt">{s.rationale}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section n="10" title="Suggested Discovery Questions">
          <ul className="clean">
            {(j.questions ?? []).map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </Section>

        <Section n="11" title="Outreach — Persuasion Engine">
          <div className="pe-intro">
            Five styles generated, scored for response rate, winner selected. Click a style to compare.
          </div>

          <div className="pe-tabs">
            {variants.map((v, i) => (
              <button
                key={i}
                className={`pe-tab${i === winner ? ' win' : ''}${i === active ? ' on' : ''}`}
                onClick={() => setActive(i)}
              >
                {i === winner ? '★ ' : ''}
                {v.style || `V${i + 1}`} <em>{total(i)}</em>
              </button>
            ))}
          </div>

          {shown && (
            <>
              <div className="pe-scores">
                {SCORE_LABELS.map(([key, label]) => {
                  const n = shown.scores?.[key] ?? 0
                  const colour = n >= 8 ? 'var(--good)' : n >= 6 ? 'var(--warm)' : 'var(--cool)'
                  return (
                    <span className="pe-score" key={key}>
                      <b style={{ color: colour }}>{n}</b>
                      {label}
                    </span>
                  )
                })}
                <span className="pe-total">
                  {total(active)}
                  <i>/50</i>
                </span>
              </div>
              <div className="email">
                <CopyButton text={`${shown.subject ? `Subject: ${shown.subject}\n\n` : ''}${shown.body ?? ''}`} />
                <pre>
                  {shown.subject ? `Subject: ${shown.subject}\n\n` : ''}
                  {shown.body}
                </pre>
              </div>
            </>
          )}

          {j.outreach?.cmoVerdict && (
            <div className="pe-cmo">
              <span className="pe-cmo-l">FTSE100 CMO verdict</span>
              {j.outreach.cmoVerdict}
            </div>
          )}
        </Section>
      </div>
    </div>
  )
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="sec">
      <div className="sec-h">
        <span className="n">{n}</span> {title}
      </div>
      {children}
    </section>
  )
}
